/**
 * Cookie consent: one dialog with two layers, and the gate every embed asks before it loads.
 *
 * <p>The answer is kept in `localStorage` and deliberately not in a cookie.
 * `PublicDelivery.CanCompress` drops Brotli and gzip for any request carrying a cookie, so a
 * consent cookie set on every visitor would quietly take the home page from 8.3 kB back to
 * 39 kB — the whole compression stage, undone by a storage choice.</p>
 *
 * <p>Nothing is gated on the server. `amund.js` is deferred, so a server-rendered frame already
 * exists in the DOM before any script could stop it; the only gate that holds is to never render
 * the frame and build it here instead. That is also why nothing in this file writes the literal
 * string for an iframe tag — `tools/check-prototype.py` fails the build on it, page and bundle
 * alike.</p>
 */
import { find, findAll, require as requireEl } from "./dom";

const KEY = "amund.consent";

/**
 * Bump to ask everyone again after the policy changes.
 *
 * <p>2: embedded content came to cover the home page's visit count, which an answer given before never
 * agreed to. The count was removed again on 2026-09-29, so an answer given under 2 now covers more than
 * is asked, and nobody needs to be asked again.</p>
 */
const VERSION = 2;

interface Consent {
  v: number;
  embeds: boolean;
  ts: number;
}

let state: Consent | null = null;

/** False once storage has thrown, which private modes do rather than returning null. */
let storageWorks = true;

const listeners: ((granted: boolean) => void)[] = [];

let dialog: HTMLDialogElement | null = null;
let toggle: HTMLInputElement | null = null;
let trigger: HTMLElement | null = null;

/** Reads the stored answer, treating anything unparseable or outdated as no answer at all. */
function read(): Consent | null {
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(KEY);
  } catch {
    storageWorks = false;
    return null;
  }

  if (raw === null) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) {
      return null;
    }

    const value = parsed as Partial<Consent>;
    if (value.v !== VERSION || typeof value.embeds !== "boolean") {
      return null;
    }

    return { v: VERSION, embeds: value.embeds, ts: typeof value.ts === "number" ? value.ts : 0 };
  } catch {
    return null;
  }
}

/** Stores the answer with its timestamp and tells every embed on the page. */
function write(embeds: boolean): void {
  state = { v: VERSION, embeds, ts: Date.now() };

  try {
    window.localStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    storageWorks = false;
  }

  for (const listener of listeners) {
    listener(embeds);
  }
}

/** True when the visitor has allowed content embedded from Google Maps and YouTube. */
export function hasEmbedConsent(): boolean {
  return state?.embeds === true;
}

/** Grants embedded content, used by the button inside a blocked embed. */
export function grantEmbeds(): void {
  decide(true);
}

/** Registers a listener that runs every time the answer changes. */
export function onConsentChange(listener: (granted: boolean) => void): void {
  listeners.push(listener);
}

/** Builds the block that stands in a blocked embed's place, with its own accept button. */
export function consentPrompt(message: string, action: string): HTMLElement {
  const box = document.createElement("div");
  box.className = "consent-prompt";

  const text = document.createElement("p");
  text.textContent = message;

  const button = document.createElement("button");
  button.type = "button";
  button.className = "btn btn--solid";
  button.textContent = action;
  button.addEventListener("click", () => {
    grantEmbeds();
  });

  box.append(text, button);
  return box;
}

/**
 * Builds the dialog once.
 *
 * <p>Both answers are the same button — same grid column, same fill, neither one primary.
 * A filled "allow" beside an outlined "reject" is precisely the weighting the EDPB's cookie
 * banner taskforce reports on, and it is what the old site and most Swedish sites still do.</p>
 */
function build(): void {
  const element = document.createElement("dialog");
  element.className = "consent";
  element.innerHTML = `
    <div class="consent__panel" data-panel="1">
      <h2 class="consent__title" id="consent-title-1" tabindex="-1">Cookies på Åmund</h2>
      <p class="consent__text">Vi använder inga cookies för spårning eller statistik. Vill du se kartan
        och våra filmer laddar vi in dem från Google och YouTube, som sätter egna cookies.</p>
      <div class="consent__actions">
        <button class="btn btn--solid" type="button" data-reject>Avvisa alla</button>
        <button class="btn btn--solid" type="button" data-accept>Tillåt alla</button>
      </div>
      <p class="consent__aside">
        <button class="consent__link" type="button" data-customise>Anpassa</button>
        <span aria-hidden="true">·</span>
        <a class="consent__link" href="/integritetspolicy/">Integritetspolicy</a>
      </p>
    </div>

    <div class="consent__panel" data-panel="2" hidden>
      <h2 class="consent__title" id="consent-title-2" tabindex="-1">Anpassa cookies</h2>
      <ul class="consent__cats">
        <li class="consent__cat">
          <span class="consent__cat-head">
            <span class="consent__cat-name">Nödvändiga</span>
            <span class="consent__cat-locked">Alltid aktiva</span>
          </span>
          <details class="consent__more">
            <summary>Vad lagras?</summary>
            <p>Ditt val i den här rutan sparas i din webbläsare. Skickar du ett formulär sätter vi en
              säkerhetscookie som skyddar mot falska inskick.</p>
          </details>
        </li>
        <li class="consent__cat">
          <span class="consent__cat-head">
            <label class="consent__switch">
              <input type="checkbox" data-embeds>
              <span class="consent__cat-name">Inbäddat innehåll</span>
            </label>
          </span>
          <details class="consent__more">
            <summary>Vad lagras?</summary>
            <p>Google Maps visar kartan på kontaktsidan. YouTube visar våra filmer. Båda sätter
              egna cookies hos Google när innehållet laddas.</p>
          </details>
        </li>
      </ul>
      <div class="consent__actions consent__actions--single">
        <button class="btn btn--solid" type="button" data-save>Spara val</button>
      </div>
      <p class="consent__aside">
        <button class="consent__link" type="button" data-back>Tillbaka</button>
      </p>
    </div>`;

  document.body.append(element);

  dialog = element;
  toggle = requireEl<HTMLInputElement>("[data-embeds]", element);

  requireEl("[data-reject]", element).addEventListener("click", () => decide(false));
  requireEl("[data-accept]", element).addEventListener("click", () => decide(true));
  requireEl("[data-save]", element).addEventListener("click", () => decide(toggle?.checked === true));
  requireEl("[data-customise]", element).addEventListener("click", () => showLayer(2));
  requireEl("[data-back]", element).addEventListener("click", () => showLayer(1));

  // The dialog only opens because the visitor asked for it from the footer, so Escape and a click
  // outside both let them out with their answer as it was. Until 2026-09-29 it also opened unasked
  // on a first visit, and Escape was held then, because dismissing it would have been consent by
  // silence.
  element.addEventListener("click", (event) => {
    if (event.target === element) {
      element.close();
    }
  });

  element.addEventListener("close", () => {
    document.documentElement.style.removeProperty("overflow");
    trigger?.focus();
  });
}

/** Swaps the visible layer and moves focus and the dialog's name along with it. */
function showLayer(layer: 1 | 2): void {
  if (dialog === null) {
    return;
  }

  for (const panel of findAll("[data-panel]", dialog)) {
    panel.hidden = panel.dataset["panel"] !== String(layer);
  }

  dialog.setAttribute("aria-labelledby", `consent-title-${layer}`);
  find(`#consent-title-${layer}`, dialog)?.focus();
}

/** Records the answer and closes. */
function decide(embeds: boolean): void {
  write(embeds);
  dialog?.close();
}

/**
 * Opens the dialog.
 *
 * <p>Modal only while storage works. With storage blocked the answer cannot be remembered, so
 * blocking the site behind a question we would have to ask again on the next page would be a
 * trap rather than a choice.</p>
 */
function open(layer: 1 | 2, from: HTMLElement | null): void {
  if (dialog === null) {
    build();
  }
  if (dialog === null) {
    return;
  }

  trigger = from;
  if (toggle !== null) {
    toggle.checked = hasEmbedConsent();
  }

  showLayer(layer);
  dialog.classList.toggle("consent--inline", !storageWorks);

  if (storageWorks) {
    // `<dialog>` makes the page behind inert but not still.
    document.documentElement.style.setProperty("overflow", "hidden");
    dialog.showModal();
    return;
  }

  dialog.show();
}

/** Opens the dialog at the given layer from a control the visitor operated. */
export function openConsent(layer: 1 | 2, from: HTMLElement | null = null): void {
  open(layer, from);
}

/**
 * Reads the stored answer and wires the footer's "Cookie-inställningar" to the dialog.
 *
 * <p>The dialog no longer asks on a first visit. Nothing that needs consent is stored until the
 * visitor asks for a map or a film, and each of those asks on the spot with a button of its own.
 * Niklas chose that on 2026-09-29, so that a first visit meets the farm rather than a question.</p>
 */
export function initConsent(): void {
  state = read();

  for (const button of findAll("[data-consent-open]")) {
    button.addEventListener("click", () => open(2, button));
  }
}
